
import { getClaimsDefaults, processPaymentOptionEdit } from "@/helpers/claims";
import * as types from "@/store/mutation-types";
import { claimsFormSchema } from "@/forms/shared/claims";
import {
  claimsMapActions,
  claimsMapGetters,
  claimsMapMutations,
  claimsMapState
} from "@/store/modules/claims";
import Vue from "vue";
import { cloneDeep, get, omit, set } from "lodash";
import { getPHPAPIFormattedDate } from "@/helpers/dateProcessor";
import {
  AtlasClaimRepresentativeOptions,
  FieldAdjusterOptions,
  KindOfLossOptions
} from "@/helpers/constants";
import { IClaimsInfo } from "@/store/modules/claims/types";
import { getLossMortgageAddress } from "@/helpers/lossFormDataProcessor";
import LossFormClaimInfo from "@/components/lossForm/LossFormClaimInfo.vue";
import {
  capitalizeFirstLetter,
  lossDateIsGreaterThan120Days
} from "@/helpers/generalHelpers";
import CustomAlert from "@/components/CustomAlert/CustomAlert.vue";
import GoogleAutoCompleteModal from "@/components/User/GoogleAutoCompleteModal.vue";
import { propertyChangeRequestMapActions } from "@/store/modules/propertychangerequest";
import { IAddress } from "@/types";

interface IClaims {
  errorMessage: string;
  receivePaymentOption: string;
  successMessage: string;
  isInsuredFieldsValid: boolean;
  isAgentFieldValid: boolean;
  isPolicyInfoValid: boolean;
  validationData: any;
  fiservData: any;
  today: string;
  lossFormRequestData: IClaimsInfo;
  customKey: number;
  loadingText: string;
  scrubbingAddressStatus: boolean;
  addressEnteredInGoogleAutoCompleteField: string;
  modalName: string;
  loading: boolean;
}

export default Vue.extend({
  name: "claims",
  components: {
    LossFormClaimInfo,
    CustomAlert,
    GoogleAutoCompleteModal
  },
  data(): IClaims {
    return {
      receivePaymentOption: "",
      errorMessage: "",
      successMessage: "",
      isInsuredFieldsValid: false,
      isAgentFieldValid: false,
      isPolicyInfoValid: false,
      validationData: {},
      fiservData: {},
      lossFormRequestData: {
        companyNumber: "",
        policyNumber: "",
        effectiveDate: "",
        companyPrefix: ""
      },
      customKey: 0,
      today: getPHPAPIFormattedDate(new Date()),
      loadingText: "",
      scrubbingAddressStatus: false,
      addressEnteredInGoogleAutoCompleteField: "",
      modalName: `googleAutoCompleteModal_${Math.random()
        .toString(36)
        .substring(2, 15)}`,
      loading: false
    };
  },
  async created() {
    this.setEdit(getClaimsDefaults({}));
    if (!this.getLossFormData && Boolean(this.claimsPolicyData)) {
      const data = this.claimsPolicyData.split("+");
      const prefix = data[0];
      const policyNumber = data[1];
      const companyNumber = data[2] == "99" ? "20" : data[2];
      const date = data[3];
      this.lossFormRequestData = {
        companyPrefix: prefix,
        policyNumber: policyNumber,
        effectiveDate: date,
        companyNumber: companyNumber
      };
      await this.getLossData(this.lossFormRequestData);
    }
    this.fiservData = this.getLossFormData || {};
    const { Branch = "", BranchName = "" } =
      this.fiservData?.lossFormOtherInfo?.Coverages || {};

    set(
      this.fiservData,
      "lossFormOtherInfo.Coverages.FormType",
      Branch && BranchName ? `${Branch} - ${BranchName}` : ""
    );

    const agentFullName = `${capitalizeFirstLetter(
      this.getValue(this.$getCurrentUser, "firstName")
    )} ${capitalizeFirstLetter(
      this.getValue(this.$getCurrentUser, "lastName")
    )}`;
    set(this.fiservData, "lossFormOtherInfo.Info.AgContacts", agentFullName);
    set(
      this.fiservData,
      "lossFormOtherInfo.Info.Email",
      this.$getCurrentUser.email
    );
    set(
      this.fiservData,
      "mortgageInfo",
      getLossMortgageAddress(this.fiservData)
    );
    set(this.fiservData, "lossFormRequire.dtSubmitted", new Date());
    set(
      this.fiservData,
      "lossFormRequire.policyNum",
      this.fiservData?.policyNumber
    );
    set(
      this.fiservData,
      "lossFormRequire.policyNum",
      get(this.fiservData, "lossFormOtherInfo.Client.PolicyNum", "") || ""
    );
    set(
      this.fiservData,
      "policyNumber",
      get(this.fiservData, "lossFormOtherInfo.Client.PolicyNum", "") || ""
    );
    set(this.fiservData, "lossFormRequire.formType", BranchName);

    set(
      this.fiservData,
      "lossFormRequire.refAgentCode",
      this.fiservData?.formItem?.agentCode
    );
    set(
      this.fiservData,
      "lossFormOtherInfo.Info.AgentCode",
      this.fiservData?.formItem?.agentCode
    );
    set(this.fiservData, "agentCode", this.fiservData?.formItem?.agentCode);
    set(
      this.fiservData,
      "companyPrefix",
      this.fiservData?.policyNumber?.split(" ")[0]
    );
    set(this.fiservData, "site", "ATLAS");
    set(this.fiservData, "role", this.$getCurrentUser.role);
  },
  props: {
    claimsPolicyData: {
      type: String
    },
    createType: {
      type: String
    }
  },
  methods: {
    ...claimsMapActions(["createClaims", "getLossData", "saveClaims"]),
    ...claimsMapMutations({
      editField: types.SET_EDIT_FIELD,
      setEdit: types.SET_EDIT,
      setStates: types.SET_STATES
    }),
    getValue(dataObject: object, key: string): string {
      return get(dataObject, key, "") || "";
    },
    async formFieldChangedHandler({
      key,
      value
    }: {
      key: string;
      value: any;
    }): Promise<void> {
      const dateFields = [
        "lossFormOtherInfo.FormInfo.LossDate",
        "lossFormOtherInfo.Coverages.DateAssigned"
      ];
      if (value instanceof Date || dateFields.includes(key)) {
        const formattedDate = getPHPAPIFormattedDate(value);
        set(this.fiservData, key, formattedDate);
      } else if (key === "lossFormOtherInfo.FormInfo.paymentOption") {
        await processPaymentOptionEdit.bind(this)(key, value, this.fiservData);
      } else if (
        key === "lossFormOtherInfo.FormInfo.ClassId" &&
        value !== "21"
      ) {
        set(this.fiservData, "lossFormOtherInfo.FormInfo.ClaimAntName", "");
        set(this.fiservData, "lossFormOtherInfo.FormInfo.ClaimAntPhone", "");
        set(this.fiservData, "lossFormOtherInfo.FormInfo.ClaimAntAddress", "");
        set(this.fiservData, key, value);
      } else {
        set(this.fiservData, key, value);
      }
      this.fiservData = cloneDeep(this.fiservData);
    },
    async toolbarSelectItem(event: string) {
      switch (event) {
        case "submit":
          await this.submitLossForm();
          break;
        case "save":
          await this.saveLossForm();
          break;
        case "cancel":
          this.$router.push("/Claims").catch(() => {});
          break;
      }
    },
    processClaimsInformation() {
      this.loadingText = "Saving claim. Please wait...";
      if (
        this.fiservData?.lossFormRequire &&
        Object.keys(this.fiservData.lossFormRequire).includes("paymentOption")
      ) {
        this.fiservData.lossFormRequire.paymentOption =
          get(
            this.fiservData,
            "lossFormOtherInfo.FormInfo.paymentOption",
            ""
          ) || "";
      }
      if (
        !lossDateIsGreaterThan120Days(
          this.getValue(this.fiservData, "lossFormOtherInfo.FormInfo.LossDate")
        ) &&
        get(
          this.fiservData,
          "lossFormOtherInfo.FormInfo.ReasonForDelayInReport",
          ""
        )
      ) {
        this.fiservData.lossFormOtherInfo.FormInfo.ReasonForDelayInReport = "";
      }
      this.fiservData.lossFormRequire = {
        ...this.fiservData.lossFormRequire,
        ...this.fiservData.formItem
      };
      //formItem.email is same as  lossFormOtherInfo.Info.Email, if not set replace
      if (!this.getValue(this.fiservData, "formItem.email")) {
        set(
          this.fiservData,
          "formItem.email",
          this.getValue(this.fiservData, "lossFormOtherInfo.Info.Email")
        );
      }

      if (!this.getValue(this.fiservData, "lossFormRequire.email")) {
        set(
          this.fiservData,
          "lossFormRequire.email",
          this.getValue(this.fiservData, "lossFormOtherInfo.Info.Email")
        );
      }

      if (!this.getValue(this.fiservData, "lossFormRequire.email")) {
        set(
          this.fiservData,
          "lossFormRequire.email",
          this.getValue(this.fiservData, "lossFormOtherInfo.Info.Email")
        );
      }
    },
    async saveLossForm() {
      try {
        const toOmit = ["mortgageInfo"];
        this.processClaimsInformation();
        await this.saveClaims({ data: omit(this.fiservData, toOmit) });
        this.$appNotifySuccess("Loss Form saved.");
        this.$router.push("/claims").catch(() => {});
      } catch (e) {
        this.errorMessage = e.message || "Error Saving Claims";
        this.$bugSnagClient.notify(e);
      } finally {
        this.loadingText = "";
      }
    },
    async submitLossForm(): Promise<void> {
      try {
        const toOmit = ["mortgageInfo"];
        this.processClaimsInformation();
        await this.createClaims({ data: omit(this.fiservData, toOmit) });
        this.$appNotifySuccess("Loss Form saved.");
        this.$router.push("/claims").catch(() => {});
      } catch (e) {
        this.errorMessage = e.message || "Error Submitting Claims";
        this.$bugSnagClient.notify(e);
      } finally {
        this.$emit("scrollTop");
        this.loadingText = "";
      }
    },

    onClickElement() {
      this.$modal.show(this.modalName);
    },
    ...propertyChangeRequestMapActions(["scrubAddress"]),
    async doScrubAddress() {
      try {
        this.setStates({ makingApiRequest: true });
        const address = await this.scrubAddress({
          formattedAddress: this.addressEnteredInGoogleAutoCompleteField as any
        });
        this.setAddressData(address);
        this.$modal.hide(this.modalName);
      } catch (error) {
        this.$bugSnagClient.notify(error);
      } finally {
        this.setStates({ makingApiRequest: false });
      }
    },
    setAddressData(address: IAddress) {
      this.formFieldChangedHandler({
        key: "lossFormOtherInfo.FormInfo.State",
        value: address?.state || ""
      });

      this.formFieldChangedHandler({
        key: "lossFormOtherInfo.FormInfo.City",
        value: address?.city || ""
      });

      this.formFieldChangedHandler({
        key: "lossFormOtherInfo.FormInfo.ZipCode",
        value: address?.zipCode || ""
      });

      this.formFieldChangedHandler({
        key: "lossFormOtherInfo.FormInfo.LocalAddress",
        value: this.addressEnteredInGoogleAutoCompleteField
      });
    }
  },
  computed: {
    ...claimsMapState(["makingApiRequest"]),
    ...claimsMapGetters(["getLossFormData"]),
    dynamicFormBuilderOptions(): any {
      return {
        "lossFormOtherInfo.FormInfo.ClassId": [...KindOfLossOptions],
        "lossFormOtherInfo.Coverages.FieldAssigned": [...FieldAdjusterOptions],
        "lossFormOtherInfo.Coverages.ClaimRepresentative": [
          ...AtlasClaimRepresentativeOptions
        ]
      };
    },
    fieldsWithIssues(): string[] {
      if (this.validationData.fieldsWithErrors) {
        return Object.values(this.validationData.fieldLabels);
      }
      return [];
    },
    claimFormFields(): any[] {
      const LossDate = this.getValue(
        this.fiservData,
        "lossFormOtherInfo.FormInfo.LossDate"
      );
      const showReasonFormDelayInReport = lossDateIsGreaterThan120Days(
        LossDate
      );
      const claimsSchema = claimsFormSchema({
        type: this.createType,
        viewType: "editing",
        otherAuthConcact: this.getValue(
          this.fiservData,
          "lossFormOtherInfo.Client.OtherAuthConcact"
        ),
        otherPhone: this.getValue(
          this.fiservData,
          "lossFormOtherInfo.Client.OtherPhone"
        ),
        windExcludeStatement: this.getValue(
          this.fiservData,
          "lossFormOtherInfo.Coverages.WindExcludeStatement"
        ),
        showReasonFormDelayInReport
      });

      return claimsSchema;
    },
    getLabel(): string {
      let label = "";
      const staticLabel = "MORTGAGE INFO.";
      const dynamicLabel = "| MORTGAGE BILLED";
      const payOrInd = get(this.fiservData, "lossFormOtherInfo.Info.PayorInd");
      if (payOrInd === "1") {
        label = `${staticLabel} ${dynamicLabel}`;
      } else if (payOrInd === "0") {
        label = `${staticLabel} Insure Billed`;
      } else {
        label = staticLabel;
      }
      return label;
    },
    primaryButtonData(): {
      text: string;
      key: string;
      disabled: boolean;
      loading: boolean;
    } {
      return {
        text: this.$isCurrentUserTPA ? "Save" : "Submit",
        key: this.$isCurrentUserTPA ? "save" : "submit",
        disabled: !this.validationData.formIsValid,
        loading: this.makingApiRequest
      };
    }
  }
});
